import React, { useEffect, useState, useMemo } from "react";
import { getRoutePatrols, getReviewerPatrolPlans, clearPatrolState, getPatrolUsers, changeDashboard } from "../../redux/actions/index";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faFolderOpen } from "@fortawesome/pro-light-svg-icons";
import PatrolsNotice from "./PatrolsNotice";
import PatrolList from "./PatrolList";
import { Input, Tooltip, Switch, Select, Button } from "antd";
import { CalendarOutlined, SearchOutlined } from "@ant-design/icons";
import Tippy from "@tippyjs/react";
import { sticky } from "tippy.js";
import DayPicker from "react-day-picker";
import logo from "../../images/new-branding-2.0/aivr-hybrid.svg";
import _ from "lodash";
import PatrolPlanDrawer from "./PatrolPlanDrawer";

const { Option } = Select;

const patrolsSelector = (state) => state.schemaInterface.patrols; // not actually patrol plans - patrols
const reviewerPatrolPlansSelector = (state) => state.schemaInterface.reviewerPatrols;
const isSupervisorSelector = (state) => state.userDetails.userConfig.is_patrol_supervisor;
const userDetailsSelector = (state) => state.userDetails;
const currentDashboardSelector = (state) => _.find(state.dashboards, (dash) => dash.access_id === state.userDetails.dashboardAccessID);
const patrolUsersSelector = (state) => state.schemaInterface.patrolUsers.users;

const PatrolNavigation = () => {
    const patrols = useSelector(patrolsSelector);
    const reviewerPatrolPlans = useSelector(reviewerPatrolPlansSelector);
    const isSupervisor = useSelector(isSupervisorSelector);
    const userDetails = useSelector(userDetailsSelector);
    const currentDashboard = useSelector(currentDashboardSelector);
    const patrolUsers = useSelector(patrolUsersSelector);

    const history = useHistory();
    const dispatch = useDispatch();

    const [searchQuery, setSearchQuery] = useState();
    const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
    const [onlyIncomplete, setOnlyIncomplete] = useState(false);
    const [archivedPatrolsVisible, setArchivedPatrolsVisible] = useState();
    const [statusFilter, setStatusFilter] = useState("all_current");
    const [sortType, setSortType] = useState(0);
    const [superUserSort, setSuperUserSort] = useState("alphabetical");
    const [userSelected, setUserSelected] = useState(undefined);
    const [planDrawerOpen, setPlanDrawerOpen] = useState(false);
    const [expandAllRows, setExpandAllRows] = useState(false);
    const [dateRangeOpen, setDateRangeOpen] = useState(false);

    const { from, to } = dateRange;
    const modifiers = { start: from, end: to };

    useEffect(() => {
        dispatch(clearPatrolState());
    }, [dispatch]);

    useEffect(() => {
        if (userDetails.email) {
            if (userDetails.userConfig.is_patrol_supervisor) {
                if (!reviewerPatrolPlans.length) {
                    console.log("debug sending get patrols request here");
                    dispatch(getPatrolUsers());
                    dispatch(getReviewerPatrolPlans());
                }
            } else {
                if (!patrols.length) {
                    console.log("debug getting non supervisor patrols here");
                    dispatch(getRoutePatrols());
                }
            }
        }
    }, [dispatch, patrols.length, reviewerPatrolPlans.length, userDetails.email, userDetails.userConfig.is_patrol_supervisor]);

    const onBackClick = () => {
        if (currentDashboard?.workspace_layout === "route_patrols") {
            dispatch(changeDashboard());
        } else {
            history.push("/");
        }
    };

    const updateSearchField = (value) => {
        setSearchQuery(value.toLowerCase());
    };

    const sameDay = (d1, d2) => {
        return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
    };

    const handleRangeReset = () => {
        setDateRange({ from: undefined, to: undefined });
    };

    const handleDayClick = (day) => {
        const range = DayPicker.DateUtils.addDayToRange(day, dateRange);
        if (range.to === undefined || range.to === null) {
            range.to = day;
            range.from = day;
        }

        setDateRange({ from: range.from, to: range.to });
    };

    const handleSwitchChange = (value) => {
        setOnlyIncomplete(value);
    };

    const handleSortChange = (value) => {
        setSortType(value);
    };

    const handleUserSelectedChange = (user) => {
        setUserSelected(user);
    };

    const toggleArchivedPatrols = () => {
        setArchivedPatrolsVisible(!archivedPatrolsVisible);
    };

    const toggleExpandAll = () => {
        setExpandAllRows((prev) => !prev);
    };

    const assignUserOptions = useMemo(() => {
        if (patrolUsers && patrolUsers.length) {
            const sorted = patrolUsers
                .sort((a, b) => {
                    let compareA = a.name || a.email;
                    let compareB = b.name || b.email;

                    if (compareA && compareB) {
                        return compareA.localeCompare(compareB);
                    } else {
                        return a;
                    }
                })
                .sort((x, y) => {
                    return x.email === userDetails.email ? -1 : y.email === userDetails.email ? 1 : 0;
                });

            return sorted.map((user) => {
                return (
                    <Option
                        key={user.id}
                        value={user.id}>
                        {user.name ? user.name : user.email}
                    </Option>
                );
            });
        }
    }, [userDetails, patrolUsers]);

    return (
        <div className="App">
            <div
                className="NavBarContainer"
                style={{ paddingTop: 0 }}>
                <Tippy
                    className="SidebarButtonContainerTippy"
                    arrow={true}
                    placement="right"
                    content={<strong>{currentDashboard?.description || currentDashboard?.access_id}</strong>}
                    theme="aivr"
                    zIndex="600"
                    delay="100">
                    <div
                        className="SidebarButtonContainer"
                        style={{ cursor: "default" }}>
                        <img
                            className="NavBarLogo"
                            src={logo}
                            alt="AIVR Logo"
                            crossOrigin={"anonymous"}
                        />
                    </div>
                </Tippy>
                {/* below required for a scroller to take up space and push bottom to the bottom of the container */}
                <div className="DashboardScroller"></div>

                <div className="NabBarBottom">
                    <div className="SidebarButtonWrap">
                        <Tippy
                            className="SidebarButtonContainerTippy"
                            arrow={true}
                            placement="right"
                            content="Patrol Diagrams"
                            theme="aivr"
                            zIndex="600"
                            delay="100">
                            <div
                                className="SidebarButtonContainer"
                                onClick={() => setPlanDrawerOpen(true)}>
                                <FontAwesomeIcon
                                    icon={faFolderOpen}
                                    style={{ fontSize: 32, color: "#e8dfff" }}
                                />
                            </div>
                        </Tippy>

                        <Tippy
                            className="SidebarButtonContainerTippy"
                            arrow={true}
                            placement="right"
                            content="Go back"
                            theme="aivr"
                            zIndex="600"
                            delay="100">
                            <div
                                className="SidebarButtonContainer"
                                onClick={onBackClick}>
                                <FontAwesomeIcon
                                    icon={faArrowLeft}
                                    style={{ fontSize: 32, color: "#e8dfff" }}
                                />
                            </div>
                        </Tippy>
                    </div>
                </div>
            </div>

            <div className="PatrolNavigation">
                <PatrolPlanDrawer
                    open={planDrawerOpen}
                    onClose={() => setPlanDrawerOpen(false)}
                />

                <div className="PatrolNavigation__Notice">{PatrolsNotice()}</div>

                <div className="PatrolNavigation__Filters">
                    <div className="PatrolNavigation__Filters__Row">
                        <div className="PatrolNavigation__Filters__Row__Inner">
                            <div>
                                <div style={{ color: "white", fontSize: "11px" }}>Patrols Status:</div>
                                <Select
                                    value={statusFilter}
                                    style={{ width: 300 }}
                                    placeholder="Filter by status"
                                    onChange={(value) => setStatusFilter(value)}>
                                    <Option
                                        key={1}
                                        value="all">
                                        All
                                    </Option>
                                    <Option
                                        key={0}
                                        value="all_current">
                                        All Unreviewed
                                    </Option>
                                    {isSupervisor && (
                                        <Option
                                            key={2}
                                            value="unassigned">
                                            Unassigned
                                        </Option>
                                    )}
                                    <Option
                                        key={3}
                                        value={0}>
                                        {isSupervisor ? "Assigned - Not Started" : "Not Started"}
                                    </Option>
                                    <Option
                                        key={4}
                                        value={1}>
                                        In Progress
                                    </Option>
                                    <Option
                                        key={5}
                                        value={2}>
                                        Awaiting Review
                                    </Option>
                                    <Option
                                        key={6}
                                        value={3}>
                                        Reviewed
                                    </Option>
                                </Select>
                            </div>
                            {isSupervisor && (
                                <div>
                                    <div style={{ color: "white", fontSize: "11px" }}>Users:</div>
                                    <Select
                                        showSearch
                                        defaultValue={userSelected}
                                        placeholder="Filter by user"
                                        optionFilterProp="children"
                                        onChange={(val) => handleUserSelectedChange(val)}
                                        allowClear
                                        filterOption={(input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                                        {assignUserOptions}
                                    </Select>
                                </div>
                            )}
                            <div>
                                <div style={{ color: "white", fontSize: "11px" }}>Sort By:</div>
                                {isSupervisor ? (
                                    <Select
                                        placeholder="Sort By"
                                        onChange={setSuperUserSort}
                                        value={superUserSort}>
                                        <Option key={"alphabetical"}>Alphabetical</Option>
                                        <Option key={"patrols_count"}>Patrols Count</Option>
                                        <Option key={"coverage_percent"}>Coverage Percent</Option>
                                        <Option key={"last_covered_time"}>Last Covered Time</Option>
                                    </Select>
                                ) : (
                                    <Select
                                        placeholder="Sort By"
                                        onChange={handleSortChange}
                                        value={sortType}>
                                        <Option
                                            key={0}
                                            value={0}>
                                            Scheduled Date of Patrol
                                        </Option>
                                        <Option
                                            key={1}
                                            value={1}>
                                            Status
                                        </Option>
                                    </Select>
                                )}
                            </div>
                            <Tooltip title="Filter by date">
                                <div style={{ alignSelf: "flex-end" }}>
                                    <Tippy
                                        placement="top"
                                        sticky={true}
                                        plugins={[sticky]}
                                        arrow={false}
                                        delay={[0, 20000]}
                                        theme="aivr"
                                        trigger={"click"}
                                        interactive={true}
                                        visible={dateRangeOpen}
                                        onClickOutside={() => setDateRangeOpen(false)}
                                        content={
                                            <div style={{ display: "flex", flexDirection: "column" }}>
                                                <DayPicker
                                                    className="Selectable"
                                                    selectedDays={[from, { from, to }]}
                                                    modifiers={modifiers}
                                                    onDayClick={handleDayClick}
                                                />
                                                <span
                                                    onClick={handleRangeReset}
                                                    className="Filter-Reset">
                                                    Reset
                                                </span>
                                            </div>
                                        }>
                                        <div
                                            className="calendarFilter"
                                            onClick={() => setDateRangeOpen((prev) => !prev)}
                                            style={{ background: dateRangeOpen ? "#50567c" : !dateRange.to ? "#414664" : "#2D81CF" }}>
                                            <CalendarOutlined className="icon" />
                                        </div>
                                    </Tippy>
                                </div>
                            </Tooltip>
                            <div style={{ alignSelf: "flex-end" }}>
                                <Input
                                    className="SearchInput"
                                    value={searchQuery}
                                    onChange={(e) => updateSearchField(e.target.value)}
                                    placeholder={`Search for a ${isSupervisor ? "plan" : "patrol"}`}
                                    style={{ height: 32 }}
                                    spellCheck={false}
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </div>
                        </div>
                    </div>
                    <div className="PatrolNavigation__Filters__Row">
                        {/* {isSupervisor && <div className="PatrolNavigation__Switch">
                        <span>Show unused patrols</span>
                        <Switch onChange={(value) => setShowUnusedPatrols(value) }/>
                    </div>} */}

                        <div className="PatrolNavigation__Switch">
                            <span>Only show incomplete</span>
                            <Switch onChange={handleSwitchChange} />
                        </div>
                        {isSupervisor && (
                            <div className="PatrolNavigation__Switch">
                                <span>Show Archived Patrols</span>
                                <Switch onChange={toggleArchivedPatrols} />
                            </div>
                        )}
                        {isSupervisor && (
                            <div style={{ alignSelf: "flex-end" }}>
                                <Button
                                    size="small"
                                    onClick={toggleExpandAll}>
                                    {expandAllRows ? "Collapse All" : "Expand All"}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="PatrolList__Container">
                    <PatrolList
                        onlyIncomplete={onlyIncomplete}
                        showUnusedPatrols={true}
                        searchQuery={searchQuery}
                        dateRange={dateRange}
                        sameDay={sameDay}
                        archivedPatrolsVisible={archivedPatrolsVisible}
                        statusFilter={statusFilter}
                        sortType={sortType}
                        superUserSort={superUserSort}
                        userSelected={userSelected}
                        isSupervisor={isSupervisor}
                        openDiagramDrawer={setPlanDrawerOpen}
                        expandAllRows={expandAllRows}
                    />
                </div>
            </div>
        </div>
    );
};

export default PatrolNavigation;
